import { useMemo, useState } from "react";
import { useLanguage } from "../../../i18n/LanguageContext";

export default function ClassificationExercise({
  exercise,
  submittingLearningExercise,
  submitLearningExercise,
  styles,
}) {
  const { language } = useLanguage();

  // =========================================================
  // Canonical English data
  // These remain the source of truth for validation.
  // =========================================================
  const categories = useMemo(() => {
    return exercise.exercise_data?.categories || [];
  }, [exercise.exercise_data?.categories]);

  const items = useMemo(() => {
    return exercise.exercise_data?.items || [];
  }, [exercise.exercise_data?.items]);

  // =========================================================
  // Display-only ES / RU translations
  // =========================================================
  const translatedData =
    exercise.translated_exercise_data || {};

  const translatedPrompt =
    language === "es" || language === "ru"
      ? exercise.translated_prompt || null
      : null;

  const translatedCategories = useMemo(() => {
    if (language !== "es" && language !== "ru") {
      return [];
    }

    return translatedData.categories || [];
  }, [language, translatedData.categories]);

  const translatedItems = useMemo(() => {
    if (language !== "es" && language !== "ru") {
      return [];
    }

    return translatedData.items || [];
  }, [language, translatedData.items]);

  // =========================================================
  // Separate legacy mixed English + Chinese display text
  // =========================================================
  const splitEnglishChinese = (text = "") => {
    const firstChineseIndex = text.search(
      /[\u3400-\u4DBF\u4E00-\u9FFF]/
    );

    if (firstChineseIndex === -1) {
      return {
        english: text.trim(),
        chinese: null,
      };
    }

    return {
      english: text
        .slice(0, firstChineseIndex)
        .trim(),

      chinese: text
        .slice(firstChineseIndex)
        .trim(),
    };
  };

  const promptParts =
    splitEnglishChinese(exercise.prompt || "");

  const englishPrompt = promptParts.english;
  const chinesePrompt = promptParts.chinese;

  // =========================================================
  // Translation lookup maps
  // Canonical English remains the lookup key.
  // =========================================================
  const categoryTranslationMap = useMemo(() => {
    const map = new Map();

    translatedItems.forEach((item) => {
      if (
        item.canonical_category &&
        item.category
      ) {
        map.set(
          item.canonical_category,
          item.category
        );
      }
    });

    categories.forEach((category, index) => {
      if (
        !map.has(category) &&
        translatedCategories[index]
      ) {
        map.set(
          category,
          translatedCategories[index]
        );
      }
    });

    return map;
  }, [
    translatedItems,
    translatedCategories,
    categories,
  ]);

  const itemTranslationMap = useMemo(() => {
    const map = new Map();

    translatedItems.forEach((item) => {
      if (
        item.canonical_text &&
        item.text
      ) {
        map.set(
          item.canonical_text,
          item.text
        );
      }
    });

    return map;
  }, [translatedItems]);

  // =========================================================
  // State
  // =========================================================
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] =
    useState(false);
  const [score, setScore] = useState(null);

  const handleSelect = (index, category) => {
    if (submitted) return;

    // category remains canonical English.
    setAnswers((prev) => ({
      ...prev,
      [index]: category,
    }));
  };

  // =========================================================
  // Submit
  // Validation remains 100% canonical English.
  // =========================================================
  const handleSubmit = async () => {
    const correctCount = items.filter(
      (item, index) =>
        answers[index] === item.category
    ).length;

    const isCorrect =
      correctCount === items.length;

    const success =
      await submitLearningExercise({
        exerciseId: exercise.id,
        answer: answers,
        isCorrect,
      });

    if (!success) return;

    setScore({
      correct: correctCount,
      total: items.length,
    });

    setSubmitted(true);
  };

  const handleTryAgain = () => {
    setAnswers({});
    setSubmitted(false);
    setScore(null);
  };

  return (
    <div
      style={{
        ...styles.emptyBox,
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        Classification
      </div>

      {/* =====================================================
          Prompt
          EN: English only
          ZH: English + Chinese
          ES/RU: English + selected translation
      ====================================================== */}

      {englishPrompt && (
        <div style={styles.quizPath}>
          {englishPrompt}
        </div>
      )}

      {language === "zh" && chinesePrompt && (
        <div
          style={{
            ...styles.quizPath,
            marginTop: "4px",
            color: "#6b7280",
          }}
        >
          {chinesePrompt}
        </div>
      )}

      {(language === "es" ||
        language === "ru") &&
        translatedPrompt && (
          <div
            style={{
              ...styles.quizPath,
              marginTop: "4px",
              color: "#6b7280",
            }}
          >
            {translatedPrompt}
          </div>
        )}

      {/* =====================================================
          Classification items
      ====================================================== */}

      <div
        style={{
          marginTop: "16px",
          display: "grid",
          gap: "12px",
        }}
      >
        {items.map((item, index) => {
          const selected = answers[index];

          const itemParts =
            splitEnglishChinese(item.text || "");

          const englishItem =
            itemParts.english;

          const chineseItem =
            itemParts.chinese;

          const translatedItem =
            itemTranslationMap.get(item.text);

          return (
            <div
              key={`${exercise.id}-${index}`}
              style={{
                padding: "12px",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
              }}
            >
              {/* Canonical English item */}
              <div
                style={{
                  marginBottom:
                    (language === "zh" &&
                      chineseItem) ||
                    translatedItem
                      ? "3px"
                      : "8px",
                }}
              >
                {englishItem}
              </div>

              {/* Chinese item */}
              {language === "zh" &&
                chineseItem && (
                  <div
                    style={{
                      color: "#6b7280",
                      marginBottom: "8px",
                      lineHeight: "1.5",
                    }}
                  >
                    {chineseItem}
                  </div>
                )}

              {/* Spanish / Russian item */}
              {(language === "es" ||
                language === "ru") &&
                translatedItem && (
                  <div
                    style={{
                      color: "#6b7280",
                      marginBottom: "8px",
                      lineHeight: "1.5",
                    }}
                  >
                    {translatedItem}
                  </div>
                )}

              <select
                style={styles.loginInput}
                value={selected || ""}
                disabled={submitted}
                onChange={(e) =>
                  handleSelect(
                    index,
                    e.target.value
                  )
                }
              >
                <option value="">
                  Select category
                </option>

                {categories.map(
                  (category, categoryIndex) => {
                    const categoryParts =
                      splitEnglishChinese(category);

                    const englishCategory =
                      categoryParts.english;

                    const chineseCategory =
                      categoryParts.chinese;

                    const translatedCategory =
                      categoryTranslationMap.get(
                        category
                      );

                    let categoryLabel =
                      englishCategory;

                    if (
                      language === "zh" &&
                      chineseCategory
                    ) {
                      categoryLabel =
                        `${englishCategory} — ${chineseCategory}`;
                    }

                    if (
                      (language === "es" ||
                        language === "ru") &&
                      translatedCategory
                    ) {
                      categoryLabel =
                        `${englishCategory} — ${translatedCategory}`;
                    }

                    return (
                      <option
                        key={`${category}-${categoryIndex}`}
                        value={category}
                      >
                        {categoryLabel}
                      </option>
                    );
                  }
                )}
              </select>

              {/* =================================================
                  Result
              ================================================= */}

              {submitted && (
                <div
                  style={{ marginTop: "8px" }}
                >
                  {selected ===
                  item.category ? (
                    <span
                      style={{
                        color: "#16a34a",
                      }}
                    >
                      Correct
                    </span>
                  ) : (
                    <span
                      style={{
                        color: "#dc2626",
                      }}
                    >
                      Incorrect · Correct
                      category:{" "}
                      {
                        splitEnglishChinese(
                          item.category
                        ).english
                      }
                      {(language === "es" ||
                        language === "ru") &&
                      categoryTranslationMap.get(
                        item.category
                      )
                        ? ` — ${categoryTranslationMap.get(
                            item.category
                          )}`
                        : ""}
                      {language === "zh" &&
                      splitEnglishChinese(
                        item.category
                      ).chinese
                        ? ` — ${
                            splitEnglishChinese(
                              item.category
                            ).chinese
                          }`
                        : ""}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* =====================================================
          Submit / Score
      ====================================================== */}

      {!submitted ? (
        <button
          style={{
            ...styles.smallPrimaryButton,
            marginTop: "16px",
          }}
          disabled={
            submittingLearningExercise ||
            Object.keys(answers).length !==
              items.length
          }
          onClick={handleSubmit}
        >
          {submittingLearningExercise
            ? "Submitting..."
            : "Submit Classification"}
        </button>
      ) : (
        <div style={{ marginTop: "16px" }}>
          <div style={{ fontWeight: "bold" }}>
            Score: {score.correct} /{" "}
            {score.total}
          </div>

          <button
            style={{
              ...styles.smallSecondaryButton,
              marginTop: "10px",
            }}
            onClick={handleTryAgain}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}